'use client';

import { useState, FormEvent } from 'react';
import { createClient } from '@/lib/supabase/client';

interface FormData {
  name: string;
  email: string;
  phone: string;
  practiceArea: string;
  message: string;
}

export default function ContactSection() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    practiceArea: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const supabase = createClient();

      // 1. Save submission to Supabase
      const { error: dbError } = await supabase
        .from('consultation_submissions')
        .insert({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          practice_area: formData.practiceArea,
          message: formData.message,
        });

      if (dbError) throw new Error(dbError.message);

      // 2. Trigger email notifications via Edge Function
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;
      const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!;

      const emailRes = await fetch(
        `${supabaseUrl}/functions/v1/send-consultation-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${supabaseAnonKey}`,
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            practiceArea: formData.practiceArea,
            message: formData.message,
          }),
        }
      );

      if (!emailRes.ok) {
        // Submission saved — email failure is non-blocking
        console.warn('Email notification failed, but submission was saved.');
      }

      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="contact" className="contact-section section-space">
      <div className="site-container contact-grid">
        {/* Left: Contact info */}
        <div className="contact-copy">
          <p className="eyebrow">Get in touch</p>
          <h2>Schedule a Free <em>Consultation</em></h2>
          <p>
            Our attorneys are ready to evaluate your matter. Contact us today — your initial consultation is confidential and completely free.
          </p>
          <dl>
            <div>
              <dt>Phone</dt>
              <dd><a href="tel:+19177374049">(917) 737-4049</a></dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd><a href="mailto:info@harringtonlaws.com">info@harringtonlaws.com</a></dd>
            </div>
            <div>
              <dt>Address</dt>
              <dd>
                350 Fifth Avenue, Suite 4100<br />
                New York, NY 10118
              </dd>
            </div>
            <div>
              <dt>Hours</dt>
              <dd>
                Mon – Fri: 8:00 AM – 7:00 PM<br />
                Sat: 9:00 AM – 2:00 PM
              </dd>
            </div>
          </dl>
        </div>

        {/* Right: Form */}
        <div className="contact-form-panel">
          <p className="form-panel-label">Request a confidential consultation</p>
          {submitted ? (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', minHeight: '405px' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#b9985f" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '24px' }}>
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <path d="m9 11 3 3L22 4" />
              </svg>
              <h3 style={{ font: '500 34px/1.15 var(--serif)', margin: '0 0 12px' }}>Message Received</h3>
              <p style={{ color: 'rgb(213,206,195)', maxWidth: '380px', fontSize: '14px' }}>
                Thank you for reaching out. A member of our team will contact you within one business day to schedule your consultation. A confirmation has been sent to your email.
              </p>
            </div>
          ) : (
            <form className="inquiry-form" onSubmit={handleSubmit}>
              <div className="input-grid">
                <label>
                  Full Name <span>*</span>
                  <input
                    required
                    placeholder="Jane Smith"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </label>
                <label>
                  Email Address <span>*</span>
                  <input
                    required
                    placeholder="jane@example.com"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </label>
              </div>
              <div className="input-grid">
                <label>
                  Phone Number
                  <input
                    placeholder="(312) 555-0000"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </label>
                <label>
                  Practice Area <span>*</span>
                  <select
                    name="practiceArea"
                    required
                    value={formData.practiceArea}
                    onChange={(e) => setFormData({ ...formData, practiceArea: e.target.value })}
                  >
                    <option value="" disabled>Select an area</option>
                    <option value="Personal Injury">Personal Injury</option>
                    <option value="Business Law">Business Law</option>
                    <option value="Family Law">Family Law</option>
                    <option value="Employment Law">Employment Law</option>
                    <option value="Real Estate">Real Estate</option>
                    <option value="Criminal Defense">Criminal Defense</option>
                  </select>
                </label>
              </div>
              <label>
                Describe Your Legal Matter <span>*</span>
                <textarea
                  name="message"
                  required
                  rows={5}
                  placeholder="Please briefly describe your legal situation..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                />
              </label>
              {error && (
                <p style={{ color: '#e05252', fontSize: '13px', margin: '0 0 8px', lineHeight: '1.5' }}>
                  {error}
                </p>
              )}
              <button
                className="button button-gold submit-button"
                type="submit"
                disabled={isLoading}
                style={{ opacity: isLoading ? 0.7 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}
              >
                {isLoading ? 'Submitting…' : 'Request Free Consultation'}
                {!isLoading && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
                    <path d="m21.854 2.147-10.94 10.939" />
                  </svg>
                )}
              </button>
              <p className="confidentiality-note">
                This form does not create an attorney-client relationship. Please do not send confidential information until we have confirmed representation.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
